import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiX, FiStar, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// Catalogue of paywall copy keyed by feature. The trigger point passes the
// matching key in via the `feature` prop; the modal pulls headline + bullets
// from here. Centralising the copy means a wording change is a one-file edit.
const FEATURE_COPY = {
  friends: {
    title: 'You’ve hit the friends limit',
    body: 'Free accounts can have up to 8 friends. Upgrade to Pro to add unlimited friends.',
    bullets: [
      'Unlimited friends, groups, and group members',
      'Ad-free experience',
      'Priority support',
    ],
  },
  friends_other: {
    title: 'They’ve hit their friends limit',
    body: 'This person’s free account is already at 8 friends. They need to upgrade to Pro (or remove a friend) before they can accept.',
    bullets: [
      'Unlimited friends, groups, and group members',
      'Ad-free experience',
      'Priority support',
    ],
    primaryLabel: 'Got it',
    hideUpgrade: true,
  },
  groups: {
    title: 'You’ve hit the groups limit',
    body: 'Free accounts can be in up to 2 groups. Upgrade to Pro for unlimited groups.',
    bullets: [
      'Unlimited friends, groups, and group members',
      'Ad-free experience',
      'Priority support',
    ],
  },
  group_size: {
    title: 'This group is full',
    body: 'Free groups are capped at 5 members. Ask the group owner to upgrade to Pro for unlimited members, or wait for someone to leave.',
    bullets: [
      'Pro owners: unlimited members per group',
      'Unlimited friends and groups',
      'Ad-free experience',
    ],
    primaryLabel: 'Got it',
    hideUpgrade: true,
  },
};

const DEFAULT_COPY = {
  title: 'Upgrade to Pro',
  body: 'Upgrade to Pro for an ad-free experience with no limits.',
  bullets: [
    'Ad-free experience',
    'Unlimited friends, groups, and members',
    'Priority support',
  ],
};

function PaywallModal({ isOpen, onClose, feature }) {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const copy = FEATURE_COPY[feature] || DEFAULT_COPY;

  const handleUpgrade = () => {
    onClose?.();
    navigate('/upgrade', { state: { reason: feature } });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-card rounded-t-lg sm:rounded-lg shadow-modal max-w-md w-full mb-20 sm:mb-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                  <SafeIcon icon={FiStar} className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-lg font-bold text-text-primary">{copy.title}</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center hover:bg-border-subtle transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            <p className="text-sm text-text-secondary mb-4">{copy.body}</p>

            <ul className="space-y-2 mb-5">
              {copy.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-2 text-sm text-text-primary">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 gap-2 sm:gap-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-lg border border-border-subtle text-text-secondary hover:bg-surface transition-colors text-sm font-semibold"
              >
                {copy.hideUpgrade ? '' : 'Not now'}
              </button>
              {!copy.hideUpgrade ? (
                <button
                  type="button"
                  onClick={handleUpgrade}
                  className="px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 bg-accent text-white hover:bg-interactive-hover"
                >
                  {copy.primaryLabel || 'See Pro'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 bg-accent text-white hover:bg-interactive-hover"
                >
                  {copy.primaryLabel || 'Got it'}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default PaywallModal;
